import { ReactNode, RefObject } from 'react';

import { motion, useTransform } from 'motion/react';

import { useCollapseScroll } from './use-collapse-scroll';

interface CollapsibleHeaderSectionProps {
  /** Title + subtitle of the expanded rendition. Fades out only (no scale). */
  expandedHeader: ReactNode;
  /** Body of the expanded rendition (cards). Scales up toward the top + fades out. */
  expandedBody: ReactNode;
  /** Compact rendition — a single-line strip (chip row, badge row). Fades in. */
  collapsed: ReactNode;
  /** The page scroll container that drives the collapse. We read its `scrollTop`. */
  scrollContainerRef: RefObject<HTMLElement | null>;
  /** Pixel height of the toolbar fully EXPANDED (header + cards). */
  expandedHeightPx: number;
  /** Pixel height of the toolbar fully COLLAPSED (chip strip). Must be < expanded. */
  collapsedHeightPx: number;
  /** Vertical offset (px) of the cards body within the expanded toolbar (below the header). */
  bodyOffsetPx?: number;
  className?: string;
}

// --- Animation tuning (fractions of the collapse distance) -----------------
/** Header has fully faded by this fraction of the scroll distance. */
const HEADER_FADE_END = 0.45;
/** Cards have fully faded by this fraction of the scroll distance. */
const CARDS_FADE_END = 0.65;
/** Cards shrink to this scale at full collapse. */
const CARDS_MIN_SCALE = 0.8;
/** Extra upward drift (px) applied to the cards as they collapse. */
const CARDS_UP_DRIFT_PX = 80;
/** Chip strip starts fading in at this fraction (after the cards are mostly gone). */
const CHIP_FADE_START = 0.35;

/**
 * Material-style collapsing toolbar built on Motion, for a SINGLE page-scroll
 * layout.
 *
 * Layout (this is what prevents the scroll "jump"):
 * - Fixed layout height of `expandedHeightPx` — it never resizes, so the
 *   page's `scrollHeight` is constant and the browser's scroll-anchor
 *   heuristic has nothing to fight.
 * - `position: sticky; top: -distance` (distance = expanded − collapsed). The
 *   wrapper physically slides up by `distance` then pins, exposing only the
 *   bottom `collapsedHeightPx` (the chip strip). Inner layers are
 *   counter-translated by `slidPx` so they stay visually anchored at the top.
 *
 * Collapse animation (three layers):
 * - Header (title + subtitle): fades out only.
 * - Cards: scale up toward the top + drift upward + fade out.
 * - Chip strip: fades in where it already sits.
 *
 * Motion writes transform/opacity to the DOM via rAF — no React re-render on
 * scroll. See `use-collapse-scroll.ts` for scroll tracking + snap-to-end.
 */
export const CollapsibleHeaderSection = ({
  expandedHeader,
  expandedBody,
  collapsed,
  scrollContainerRef,
  expandedHeightPx,
  collapsedHeightPx,
  bodyOffsetPx = 0,
  className,
}: CollapsibleHeaderSectionProps) => {
  const distance = Math.max(1, expandedHeightPx - collapsedHeightPx);
  const { scrollY } = useCollapseScroll(scrollContainerRef, distance);

  const clamp = (value: number) => Math.min(Math.max(value, 0), distance);

  // How far the sticky wrapper has slid up (rounded to whole px so the
  // counter-translate stays aligned with the browser's sticky offset — sub-
  // pixel mismatch is what caused the "little jumps").
  const slidPx = useTransform(scrollY, latest => Math.round(clamp(latest)));

  const headerOpacity = useTransform(scrollY, [0, distance * HEADER_FADE_END], [1, 0], {
    clamp: true,
  });

  const cardsScale = useTransform(scrollY, [0, distance], [1, CARDS_MIN_SCALE], { clamp: true });
  const cardsOpacity = useTransform(scrollY, [0, distance * CARDS_FADE_END], [1, 0], {
    clamp: true,
  });
  // Counter-translate (`slidPx`) keeps the cards anchored, minus an upward
  // drift that grows with collapse so they visibly rise (overlapping the
  // header above is intentional).
  const cardsY = useTransform(scrollY, latest => {
    const progress = clamp(latest) / distance;
    return Math.round(clamp(latest)) - progress * CARDS_UP_DRIFT_PX;
  });

  const chipOpacity = useTransform(scrollY, [distance * CHIP_FADE_START, distance], [0, 1], {
    clamp: true,
  });

  // Only the visible layer should capture pointer events. Without this the
  // invisible collapsed chip strip (painted on top) still intercepts hover over
  // the expanded cards' empty top strip, showing its tooltips spuriously.
  const cardsPointerEvents = useTransform(cardsOpacity, value => (value > 0.01 ? 'auto' : 'none'));
  const chipPointerEvents = useTransform(chipOpacity, value => (value > 0.01 ? 'auto' : 'none'));

  return (
    <div
      className={`sticky shrink-0 overflow-hidden ${className ?? ''}`}
      style={{ top: -distance, height: expandedHeightPx }}
    >
      <motion.div
        className="absolute inset-x-0 top-0"
        style={{ y: slidPx, opacity: headerOpacity }}
      >
        {expandedHeader}
      </motion.div>

      <motion.div
        className="absolute inset-x-0"
        style={{
          top: bodyOffsetPx,
          transformOrigin: 'top center',
          y: cardsY,
          scale: cardsScale,
          opacity: cardsOpacity,
          pointerEvents: cardsPointerEvents,
        }}
      >
        {expandedBody}
      </motion.div>

      {/* Rendered last so it paints over the cards as they fade. */}
      <motion.div
        className="absolute inset-x-0 top-0"
        style={{
          height: collapsedHeightPx,
          y: slidPx,
          opacity: chipOpacity,
          pointerEvents: chipPointerEvents,
        }}
      >
        {collapsed}
      </motion.div>
    </div>
  );
};
